import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getDataPromise } from 'brock-fetch-utils';
import { ErrorAlert } from 'smartsuite-ui';
import { handleErrorResult } from '../utils/fetchUtils';
import { setupViewAndWidgetConfigurations } from '../utils/configurationAndValidationUtils';
import { getSelectOptionsPromise } from '../../shared/utilities/selectOptionsUtils';
import mapConfigurationVersion from '../utils/configVersionMappers/mapConfigurationVersion';
import { useFeatureTokenContext } from '../../shared/hooks/featureTokensContext';

function getThresholdsPromise(uri, setError) {
    return getDataPromise(uri).then(
        (thresholds) => thresholds,
        (error) => handleErrorResult(error, setError)
    );
}

function getSystemManifestPromise(uri, setError) {
    return getDataPromise(uri).then(
        (systemManifest) => systemManifest,
        (error) => handleErrorResult(error, setError)
    );
}

export default function InitialState(props) {
    const [state, setState] = useState({});
    const { isFeatureConditionValid, isFeatureConditionMet } =
        useFeatureTokenContext();

    function setError(errorMessage) {
        setState((prevState) => ({
            ...prevState,
            initialStateError: errorMessage,
        }));
    }

    /* eslint-disable complexity */
    useEffect(() => {
        function filterByFeatureCondition(condition) {
            return (
                !isFeatureConditionValid(condition) ||
                isFeatureConditionMet(condition)
            );
        }

        function filterPanelTabsByFeatureCondition(visiblePanels) {
            const panels = [];
            visiblePanels.forEach((panel) => {
                if (!panel.tabs) {
                    panels.push(panel);
                } else {
                    const tabs = panel.tabs.filter((tab) =>
                        filterByFeatureCondition(tab.conditionalFeature)
                    );
                    panels.push({ ...panel, tabs });
                }
            });
            return panels;
        }
        function getVisiblePanels(panels) {
            const visiblePanels = panels.filter((panel) =>
                filterByFeatureCondition(panel.conditionalFeature)
            );

            return filterPanelTabsByFeatureCondition(visiblePanels);
        }

        function getVisibleWidgets(widgets) {
            return widgets.filter((widget) =>
                filterByFeatureCondition(widget.conditionalFeature)
            );
        }
        async function initialize() {
            const dashboardConfiguration = await getDataPromise(
                props.uriConstants.getDashboardConfigurationUri(
                    props.dashboardKey
                )
            ).then(
                (response) => mapConfigurationVersion(response.configuration),
                () => setError(`Dashboard ${props.dashboardKey} does not exist`)
            );

            if (dashboardConfiguration.views[0].isUsed === false) {
                setError(`Dashboard ${props.dashboardKey} is disabled`);
            }

            const filterConfigurations = dashboardConfiguration.views.reduce(
                (result, view) => ({
                    ...result,
                    ...view.filterConfigurations,
                }),
                {}
            );

            let systemManifest = [];

            if (props.uriConstants.getSystemManifestUri) {
                systemManifest = await getSystemManifestPromise(
                    props.uriConstants.getSystemManifestUri,
                    setError
                );
            }

            const [thresholdValues, selectOptions] = await Promise.all([
                getThresholdsPromise(
                    props.uriConstants.thresholdsUri,
                    setError
                ),
                getSelectOptionsPromise(
                    props.uriConstants.selectOptionsUri,
                    filterConfigurations,
                    setError
                ),
            ]);

            const validatedViews = dashboardConfiguration.views.reduce(
                (result, view) => {
                    const filteredFeatureTogglePanels = getVisiblePanels(
                        view.panels
                    );
                    const filteredFeatureToggleWidgets = getVisibleWidgets(
                        view.widgets
                    );
                    const featureFilteredView = {
                        ...view,
                        panels: filteredFeatureTogglePanels,
                        widgets: filteredFeatureToggleWidgets,
                    };

                    const validatedView = setupViewAndWidgetConfigurations(
                        props.widgetRenderers,
                        featureFilteredView
                    );

                    return {
                        ...result,
                        [view.viewKey]: {
                            ...validatedView,
                            siteMapKey: dashboardConfiguration.siteMapKey,
                        },
                    };
                },
                {}
            );

            setState((prevState) => ({
                ...prevState,
                initialStateLoaded: true,
                selectOptions,
                thresholdValues,
                views: validatedViews,
                systemManifest,
            }));
        }

        initialize();
    }, [
        props.dashboardKey,
        props.widgetRenderers,
        props.uriConstants,
        props.favoriteUtilities,
        isFeatureConditionMet,
        isFeatureConditionValid,
    ]);

    if (state.initialStateError) {
        return <ErrorAlert>{state.initialStateError}</ErrorAlert>;
    }

    return props.render({
        initialStateLoaded: state.initialStateLoaded,
        selectOptions: state.selectOptions,
        thresholdValues: state.thresholdValues,
        views: state.views,
        systemManifest: state.systemManifest,
    });
}

InitialState.propTypes = {
    dashboardKey: PropTypes.string.isRequired,
    favoriteUtilities: PropTypes.instanceOf(Object).isRequired,
    render: PropTypes.func.isRequired,
    uriConstants: PropTypes.instanceOf(Object).isRequired,
    widgetRenderers: PropTypes.instanceOf(Object).isRequired,
};
